import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Calendar, Trash2, Download, RefreshCw, Plus, Mail, Search } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MEAL_TYPES = ['breakfast', 'lunch', 'dinner'];
const STORAGE_KEY = 'masala-spin-meal-plan';

const createEmptyPlan = () => {
  const emptyPlan = {};
  DAYS.forEach(day => {
    emptyPlan[day] = { breakfast: null, lunch: null, dinner: null };
  });
  return emptyPlan;
};

const loadPlanFromStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validate structure
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed;
      }
    }
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to parse saved meal plan:', err);
    }
  }
  return createEmptyPlan();
};

export const MealPlanner = ({ lastSpinResult }) => {
  const [mealPlan, setMealPlan] = useState(loadPlanFromStorage);
  const [selectedDay, setSelectedDay] = useState(DAYS[0]);
  const [selectedMealType, setSelectedMealType] = useState(null);

  // Manual picker state
  const [picker, setPicker] = useState({ open: false, day: null, mealType: null });
  const [allRecipes, setAllRecipes] = useState([]);
  const [search, setSearch] = useState('');

  // Email export state
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState(() => localStorage.getItem('masala-spin-email') || '');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (allRecipes.length > 0) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/recipes`);
        setAllRecipes(data);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error('recipes fetch failed', err);
      }
    })();
  }, [allRecipes.length]);

  const filteredForPicker = useMemo(() => {
    if (!picker.open) return [];
    const q = search.trim().toLowerCase();
    let pool = allRecipes;
    if (picker.mealType === 'breakfast') {
      pool = pool.filter((r) => r.category === 'breakfast');
    } else if (picker.mealType === 'lunch') {
      pool = pool.filter((r) => r.category === 'lunch');
    } else if (picker.mealType === 'dinner') {
      pool = pool.filter((r) => r.category === 'dinner' || r.category === 'crazy');
    }
    if (q) pool = pool.filter((r) => r.name.toLowerCase().includes(q));
    return pool.slice(0, 60);
  }, [picker, allRecipes, search]);

  // Save to localStorage whenever mealPlan changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(mealPlan));
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save meal plan:', err);
      }
    }
  }, [mealPlan]);

  const addMealToPlan = useCallback((day, mealType, recipe) => {
    setMealPlan(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [mealType]: recipe
      }
    }));
    toast.success(`Added ${recipe.name} to ${day}'s ${mealType}!`);
    setSelectedMealType(null);
  }, []);

  const removeMealFromPlan = useCallback((day, mealType) => {
    setMealPlan(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [mealType]: null
      }
    }));
    toast.info('Meal removed from plan');
  }, []);

  const clearAllMeals = useCallback(() => {
    setMealPlan(createEmptyPlan());
    toast.success('Meal plan cleared!');
  }, []);

  const getTotalCalories = useCallback((day) => {
    const meals = mealPlan[day];
    if (!meals) return 0;
    let total = 0;
    MEAL_TYPES.forEach(type => {
      if (meals[type]) {
        total += Number(meals[type].calories) || 0;
      }
    });
    return total;
  }, [mealPlan]);

  const exportPlan = useCallback(() => {
    let text = 'MASALA SPIN - Weekly Meal Plan\n';
    text += '================================\n\n';
    
    DAYS.forEach(day => {
      text += `${day}\n`;
      text += `   Calories: ${getTotalCalories(day)} cal\n`;
      MEAL_TYPES.forEach(type => {
        const meal = mealPlan[day][type];
        text += `   ${type.charAt(0).toUpperCase() + type.slice(1)}: ${meal ? meal.name : '(not planned)'}\n`;
      });
      text += '\n';
    });

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'masala-spin-meal-plan.txt';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Meal plan exported!');
  }, [mealPlan, getTotalCalories]);

  const sendEmail = useCallback(async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/meal-plan/email`, { email, plan: mealPlan });
      localStorage.setItem('masala-spin-email', email);
      toast.success(`Plan sent to ${email}!`);
      setEmailOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send email — try again.');
    } finally {
      setSending(false);
    }
  }, [email, mealPlan]);

  return (
    <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-6 max-w-4xl mx-auto" data-testid="meal-planner">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#1C1917]/10">
        <div className="flex items-center gap-3">
          <Calendar className="w-8 h-8 text-[#F97316]" />
          <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1C1917]">
            Weekly Meal Planner
          </h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEmailOpen(true)}
            data-testid="email-plan-button"
            className="border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white rounded-full"
          >
            <Mail className="w-4 h-4 mr-1" />
            Email me
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportPlan}
            data-testid="export-plan-button"
            className="border-[#2D4A22] text-[#2D4A22] hover:bg-[#2D4A22] hover:text-white rounded-full"
          >
            <Download className="w-4 h-4 mr-1" />
            Export
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={clearAllMeals}
            data-testid="clear-plan-button"
            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white rounded-full"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Clear All
          </Button>
        </div>
      </div>

      {/* Add from last spin result */}
      {lastSpinResult && (
        <div className="mb-6 p-4 bg-gradient-to-r from-[#F97316]/10 to-[#FBBF24]/10 rounded-2xl border border-[#F97316]/20">
          <p className="text-sm font-['Outfit'] text-[#1C1917]/70 mb-2">Last spin result:</p>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-['Outfit'] font-semibold text-[#1C1917]">{lastSpinResult.name}</p>
              <p className="text-xs text-[#1C1917]/60">{lastSpinResult.calories} cal • {lastSpinResult.prep_time}</p>
            </div>
            {selectedMealType ? (
              <div className="flex gap-2">
                {DAYS.map(day => (
                  <Button
                    key={day}
                    size="sm"
                    onClick={() => addMealToPlan(day, selectedMealType, lastSpinResult)}
                    className="bg-[#F97316] hover:bg-[#EA580C] text-white text-xs rounded-full px-3"
                  >
                    {day.slice(0, 3)}
                  </Button>
                ))}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedMealType(null)}
                  className="text-[#1C1917]/60"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                {MEAL_TYPES.map(type => (
                  <Button
                    key={type}
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedMealType(type)}
                    data-testid={`add-to-${type}-button`}
                    className="border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white rounded-full capitalize"
                  >
                    + {type}
                  </Button>
                ))}
              </div>
            )}
          </div>
          {selectedMealType && (
            <p className="text-xs text-[#F97316] mt-2 font-['Outfit']">
              Select a day to add this to {selectedMealType}
            </p>
          )}
        </div>
      )}

      {/* Weekly Grid */}
      <ScrollArea className="h-[400px]">
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
          {DAYS.map(day => (
            <div
              key={day}
              className={`p-3 rounded-2xl border transition-all duration-300 ${
                selectedDay === day 
                  ? 'bg-[#F97316]/10 border-[#F97316]' 
                  : 'bg-white/50 border-[#1C1917]/10 hover:border-[#F97316]/50'
              }`}
              onClick={() => setSelectedDay(day)}
            >
              <div className="text-center mb-3">
                <p className="font-['Outfit'] font-semibold text-[#1C1917] text-sm">{day}</p>
                <p className="text-xs text-[#F97316] font-['Outfit']">{getTotalCalories(day)} cal</p>
              </div>
              
              <div className="space-y-2">
                {MEAL_TYPES.map(type => {
                  const meal = mealPlan[day][type];
                  return (
                    <div
                      key={type}
                      className={`p-2 rounded-xl text-xs ${
                        meal 
                          ? 'bg-[#2D4A22]/10 border border-[#2D4A22]/20' 
                          : 'bg-[#1C1917]/5 border border-dashed border-[#1C1917]/20'
                      }`}
                    >
                      <p className="text-[#1C1917]/50 uppercase tracking-wide text-[10px] mb-1">
                        {type}
                      </p>
                      {meal ? (
                        <div className="flex items-start justify-between gap-1">
                          <p className="font-['Outfit'] text-[#1C1917] font-medium leading-tight">
                            {meal.name.length > 12 ? meal.name.slice(0, 12) + '...' : meal.name}
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeMealFromPlan(day, type);
                            }}
                            className="text-red-400 hover:text-red-600 flex-shrink-0"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPicker({ open: true, day, mealType: type });
                            setSearch('');
                          }}
                          data-testid={`add-meal-${day}-${type}`}
                          className="w-full flex items-center justify-center gap-1 text-[#1C1917]/40 hover:text-[#F97316] italic transition-colors"
                        >
                          <Plus className="w-3 h-3" />
                          Add
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Instructions */}
      <div className="mt-4 pt-4 border-t border-[#1C1917]/10">
        <p className="text-xs text-[#1C1917]/50 font-['Outfit'] text-center">
          Tap "+ Add" on any slot to pick a recipe, or spin the wheel for suggestions. Saved automatically · email it to yourself anytime.
        </p>
      </div>

      {/* Manual recipe picker dialog */}
      {picker.open && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPicker({ open: false, day: null, mealType: null })}
          data-testid="meal-picker-overlay"
        >
          <div
            className="bg-white rounded-3xl max-w-lg w-full max-h-[80vh] flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-[#1C1917]/10">
              <p className="text-xs uppercase tracking-widest text-[#F97316] font-['Outfit'] font-semibold">
                {picker.day} • {picker.mealType}
              </p>
              <h4 className="font-['Playfair_Display'] text-xl font-bold text-[#1C1917]">Pick a recipe</h4>
              <div className="relative mt-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1C1917]/40" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search recipes…"
                  data-testid="meal-picker-search"
                  className="pl-9 border-[#1C1917]/15"
                />
              </div>
            </div>
            <ScrollArea className="flex-1 max-h-[55vh]">
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {filteredForPicker.length === 0 && (
                  <p className="col-span-2 text-center py-8 text-[#1C1917]/50 font-['Outfit']">No matches.</p>
                )}
                {filteredForPicker.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => {
                      addMealToPlan(picker.day, picker.mealType, r);
                      setPicker({ open: false, day: null, mealType: null });
                    }}
                    data-testid={`pick-recipe-${r.id}`}
                    className="text-left p-3 rounded-xl bg-[#FDFBF7] hover:bg-[#FEF3E2] hover:shadow-sm transition-all border border-transparent hover:border-[#F97316]/20"
                  >
                    <p className="font-['Outfit'] font-semibold text-sm text-[#1C1917] leading-tight mb-1">{r.name}</p>
                    <p className="text-[11px] text-[#1C1917]/55 font-['Outfit']">
                      {r.calories} cal · {r.prep_time}{r.region ? ` · ${r.region}` : ''}
                    </p>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>
      )}

      {/* Email export dialog */}
      {emailOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setEmailOpen(false)}
          data-testid="meal-email-overlay"
        >
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h4 className="font-['Playfair_Display'] text-xl font-bold text-[#1C1917] mb-2">Email this plan</h4>
            <p className="text-sm text-[#1C1917]/65 font-['Outfit'] mb-4">
              We'll send a clean week-at-a-glance to your inbox. Save it, share it, print it.
            </p>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="meal-email-input"
              className="mb-4 border-[#1C1917]/15"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setEmailOpen(false)} className="rounded-full">Cancel</Button>
              <Button
                onClick={sendEmail}
                disabled={sending}
                data-testid="meal-email-send"
                className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-full"
              >
                <Mail className="w-4 h-4 mr-1" />
                {sending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
